var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g = Object.create((typeof Iterator === "function" ? Iterator : Object).prototype);
    return g.next = verb(0), g["throw"] = verb(1), g["return"] = verb(2), typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
/**
 * Vocabulary derivation rule administration — list + detail (CZNA-365, epic
 * CZNA-297, child 3/4).
 *
 * Settings screen for the data-driven open-vocabulary derivation registry
 * (see `vocabulary_derivation_rule`, CZNA-363). Small registry — the list
 * endpoint returns every rule in one shot (no server paging), so this renders
 * all rows directly rather than using `MultiColumnList`'s
 * `onNeedMoreData`/prev-next paging like `ValidationRulesList`. A client-side
 * text filter narrows by vocabularyType and by any of the rule's source subfields.
 *
 * Detail pane driven by the URL query string (`?selected=id` / `?create=1`),
 * mirroring `ValidationRulesList`. A per-row "Derive now" action opens the
 * same dry-run-first confirm flow as the detail pane's button (AC4,
 * "Přegenerovat").
 */
import React, { useCallback, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useHistory, useLocation } from 'react-router-dom';
import { useStripes } from '@folio/stripes/core';
import { Paneset, Pane, PaneMenu, Button, MultiColumnList, SearchField, AccordionSet, Accordion, Checkbox, MessageBanner, Callout, Layout, Headline, ConfirmationModal, } from '@folio/stripes/components';
import NavPaneLayout from '../components/NavPaneLayout';
import PaneHelpButton from '../components/PaneHelpButton';
import ThresholdStrategyBadge from '../components/DerivationRuleAdmin/ThresholdStrategyBadge';
import DerivationRuleDetail from '../components/DerivationRuleAdmin/DerivationRuleDetail';
import DerivationHelpContent from '../components/DerivationRuleAdmin/DerivationHelpContent';
import { useVocabularyDerivationRules, useSaveVocabularyDerivationRule, useDeriveVocabularyDerivationRule, } from '../hooks/useVocabularyDerivationRules';
import { getErrorInfo } from '../utils/getErrorCode';
import { getErrorKind } from '../utils/errorKind';
import { formatDerivationSource, formatDerivationSources } from '../utils/formatDerivationSource';
import { TableLoadingSkeleton } from '../components/TableStates';
var WRITE_PERM = 'czna-authorities.admin.vocabulary-derivation-rules.write';
export var COLUMN_WIDTHS = {
    enabled: '80px',
    vocabularyType: '260px',
    field: '110px',
    thresholdStrategy: '170px',
    idPrefix: '140px',
    updatedAt: '160px',
    deriveNow: '140px',
};
var DerivationRulesList = function () {
    var _a;
    var intl = useIntl();
    var history = useHistory();
    var location = useLocation();
    var stripes = useStripes();
    var canWrite = stripes.hasPerm(WRITE_PERM);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    var calloutRef = React.useRef(null);
    var _b = useState(''), query = _b[0], setQuery = _b[1];
    var _c = useState(true), filterPaneVisible = _c[0], setFilterPaneVisible = _c[1];
    var _d = useState(null), deriveRule = _d[0], setDeriveRule = _d[1];
    var _f = useState(null), derivePreview = _f[0], setDerivePreview = _f[1];
    var _g = useState(null), deriveError = _g[0], setDeriveError = _g[1];
    var _h = useVocabularyDerivationRules(), data = _h.data, isLoading = _h.isLoading, isError = _h.isError;
    var rules = useMemo(function () {
        var allRules = data !== null && data !== void 0 ? data : [];
        var q = query.trim().toLowerCase();
        if (!q)
            return allRules;
        return allRules.filter(function (r) { return (r.vocabularyType.toLowerCase().includes(q)
            // CZNA-565 — a rule has several sources; matching only the first would hide a
            // rule from a search for the very subfield the admin is looking for.
            || r.sources.some(function (src) { return formatDerivationSource(src).toLowerCase().includes(q); })
            || r.idPrefix.toLowerCase().includes(q)); });
    }, [data, query]);
    // selected rule id / create mode derived from the URL
    var search = new URLSearchParams(location.search);
    var selectedId = (_a = search.get('selected')) !== null && _a !== void 0 ? _a : undefined;
    var isCreating = search.get('create') === '1';
    var openRule = useCallback(function (rule) {
        var sp = new URLSearchParams(location.search);
        sp.delete('create');
        sp.set('selected', rule.id);
        history.push({ search: "?".concat(sp.toString()) });
    }, [history, location.search]);
    var openCreate = useCallback(function () {
        var sp = new URLSearchParams(location.search);
        sp.delete('selected');
        sp.set('create', '1');
        history.push({ search: "?".concat(sp.toString()) });
    }, [history, location.search]);
    var closeDetail = useCallback(function () {
        var sp = new URLSearchParams(location.search);
        sp.delete('selected');
        sp.delete('create');
        history.push({ search: sp.toString() ? "?".concat(sp.toString()) : '' });
    }, [history, location.search]);
    // Inline enabled toggle — PUT with the existing values + flipped enabled.
    var toggleMutation = useSaveVocabularyDerivationRule({
        onSuccess: function () {
            var _a;
            (_a = calloutRef.current) === null || _a === void 0 ? void 0 : _a.sendCallout({
                type: 'success',
                message: intl.formatMessage({ id: 'ui-czna-authorities.derivationRules.toggle.success' }),
            });
        },
        onError: function (err) { return __awaiter(void 0, void 0, void 0, function () {
            var info, kind;
            var _a;
            return __generator(this, function (_b) {
                switch (_b.label) {
                    case 0: return [4 /*yield*/, getErrorInfo(err)];
                    case 1:
                        info = _b.sent();
                        kind = getErrorKind(info);
                        (_a = calloutRef.current) === null || _a === void 0 ? void 0 : _a.sendCallout({
                            type: 'error',
                            // CZNA-1625 — no answer in time: the toggle may have been applied.
                            message: kind === 'timeout'
                                ? intl.formatMessage({ id: 'ui-czna-authorities.error.timeout' })
                                : info.status === 409
                                    ? intl.formatMessage({ id: 'ui-czna-authorities.derivationRules.save.conflict' })
                                    : intl.formatMessage({ id: 'ui-czna-authorities.derivationRules.toggle.error' }),
                        });
                        return [2 /*return*/];
                }
            });
        }); },
    });
    var handleToggle = useCallback(function (rule) {
        toggleMutation.mutate({
            mode: 'update',
            id: rule.id,
            body: {
                expectedUpdatedAt: rule.updatedAt,
                // Full-replace PUT: echo the sources back unchanged — this action only flips
                // `enabled`, and omitting them would clear the rule's source list.
                sources: rule.sources.map(function (_a) {
                    var fieldTag = _a.fieldTag, subfieldCode = _a.subfieldCode, componentType = _a.componentType;
                    return ({
                        fieldTag: fieldTag,
                        subfieldCode: subfieldCode,
                        componentType: componentType,
                    });
                }),
                idPrefix: rule.idPrefix,
                thresholdStrategy: rule.thresholdStrategy,
                minFrequency: rule.minFrequency,
                coveragePercentile: rule.coveragePercentile,
                whitelist: rule.whitelist,
                enabled: !rule.enabled,
                autoOnImport: rule.autoOnImport,
            },
        });
    }, [toggleMutation]);
    // Top-level "derive now" mutation for the per-row action (separate instance
    // from the one inside DerivationRuleDetail so list-level dry-run/confirm
    // doesn't interfere with an open detail pane's own flow).
    var deriveMutation = useDeriveVocabularyDerivationRule({
        onSuccess: function (report, vars) {
            var _a;
            // Residual staleness guard (nice-to-have on top of the isLoading guard
            // in handleDeriveNowRow above): if deriveRule has since changed (or was
            // cleared) since THIS mutation was fired, its response no longer
            // corresponds to what's on screen — ignore it rather than showing a
            // preview/confirmation for the wrong rule.
            if (!deriveRule || deriveRule.id !== vars.ruleId) {
                return;
            }
            if (report.dryRun) {
                setDeriveError(null);
                setDerivePreview(report);
            }
            else {
                setDerivePreview(null);
                (_a = calloutRef.current) === null || _a === void 0 ? void 0 : _a.sendCallout({
                    type: 'success',
                    message: intl.formatMessage({ id: 'ui-czna-authorities.derivationRules.derive.success' }, {
                        created: report.termsCreated,
                        existing: report.termsAlreadyPresent,
                        rematched: report.componentsRematched,
                    }),
                });
                setDeriveRule(null);
            }
        },
        onError: function (err, vars) { return __awaiter(void 0, void 0, void 0, function () {
            var info;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, getErrorInfo(err)];
                    case 1:
                        info = _a.sent();
                        setDerivePreview(null);
                        // CZNA-1625 — a real run with no answer in time may have written terms; a dry run writes nothing.
                        setDeriveError(!(vars === null || vars === void 0 ? void 0 : vars.dryRun) && getErrorKind(info) === 'timeout'
                            ? intl.formatMessage({ id: 'ui-czna-authorities.error.timeout' })
                            : info.message || intl.formatMessage({ id: 'ui-czna-authorities.derivationRules.derive.error' }));
                        return [2 /*return*/];
                }
            });
        }); },
    });
    // Guard against firing a second dry-run while one is already in flight
    // (code review S2): the per-row button's `disabled={deriveMutation.isLoading}`
    // (see the `deriveNow` formatter below) closes this race on RE-RENDER, but a
    // rapid click-A-then-click-B before React re-renders with `isLoading=true`
    // could still start a second dry-run for a different rule while the first
    // one is still in flight. If that stale first response arrives after
    // `deriveRule` has already been overwritten by the second click, the
    // confirmation modal would show rule A's preview counts while `deriveRule`
    // points at rule B — so confirming would derive B for real under numbers
    // that were never computed for it. The early-return here is defense in
    // depth on top of the `disabled` prop, checked synchronously at click time.
    var handleDeriveNowRow = useCallback(function (rule) {
        if (deriveMutation.isLoading)
            return;
        setDeriveRule(rule);
        setDeriveError(null);
        deriveMutation.mutate({ ruleId: rule.id, dryRun: true });
    }, [deriveMutation]);
    var handleConfirmDeriveRow = useCallback(function () {
        if (!deriveRule)
            return;
        deriveMutation.mutate({ ruleId: deriveRule.id, dryRun: false });
    }, [deriveMutation, deriveRule]);
    var handleCancelDeriveRow = useCallback(function () {
        setDerivePreview(null);
        setDeriveRule(null);
    }, []);
    var handleRowClick = useCallback(function (_e, row) {
        if (!(row === null || row === void 0 ? void 0 : row.id))
            return;
        openRule(row);
    }, [openRule]);
    var columnMapping = useMemo(function () { return ({
        enabled: React.createElement(FormattedMessage, { id: "ui-czna-authorities.derivationRules.column.enabled" }),
        vocabularyType: React.createElement(FormattedMessage, { id: "ui-czna-authorities.derivationRules.column.vocabularyType" }),
        field: React.createElement(FormattedMessage, { id: "ui-czna-authorities.derivationRules.column.field" }),
        thresholdStrategy: React.createElement(FormattedMessage, { id: "ui-czna-authorities.derivationRules.column.thresholdStrategy" }),
        idPrefix: React.createElement(FormattedMessage, { id: "ui-czna-authorities.derivationRules.column.idPrefix" }),
        updatedAt: React.createElement(FormattedMessage, { id: "ui-czna-authorities.derivationRules.column.updatedAt" }),
        deriveNow: React.createElement(FormattedMessage, { id: "ui-czna-authorities.derivationRules.column.deriveNow" }),
    }); }, []);
    var formatter = useMemo(function () { return ({
        enabled: function (r) { return (React.createElement(Checkbox, { checked: r.enabled, disabled: !canWrite || toggleMutation.isLoading, "aria-label": intl.formatMessage({ id: 'ui-czna-authorities.derivationRules.toggle.aria' }, { vocabularyType: r.vocabularyType }), onClick: function (e) { return e.stopPropagation(); }, onChange: function () { return handleToggle(r); } })); },
        vocabularyType: function (r) { return r.vocabularyType; },
        field: function (r) { return formatDerivationSources(r.sources); },
        thresholdStrategy: function (r) { return React.createElement(ThresholdStrategyBadge, { strategy: r.thresholdStrategy }); },
        idPrefix: function (r) { return r.idPrefix; },
        updatedAt: function (r) { return (r.updatedAt
            ? "".concat(intl.formatDate(r.updatedAt, { day: '2-digit', month: '2-digit', year: 'numeric' }), " ").concat(intl.formatTime(r.updatedAt, { hour: '2-digit', minute: '2-digit' }))
            : intl.formatMessage({ id: 'ui-czna-authorities.derivationRules.updatedAt.never' })); },
        deriveNow: function (r) { return (React.createElement(Button, { marginBottom0: true, buttonStyle: "default", disabled: !r.enabled || deriveMutation.isLoading, onClick: function (e) { e.stopPropagation(); handleDeriveNowRow(r); }, "data-testid": "derive-now-row-".concat(r.id) },
            React.createElement(FormattedMessage, { id: "ui-czna-authorities.derivationRules.action.deriveNow" }))); },
    }); }, [canWrite, toggleMutation.isLoading, deriveMutation.isLoading, intl, handleToggle, handleDeriveNowRow]);
    var renderContent = function () {
        // CZNA-538 — see AuthoritiesList.
        if (isLoading) {
            return React.createElement(TableLoadingSkeleton, { testId: "derivation-rules-loading", columns: [4, 6, 3, 3], rows: 6 });
        }
        if (isError) {
            return (React.createElement(MessageBanner, { type: "error" },
                React.createElement(FormattedMessage, { id: "ui-czna-authorities.derivationRules.list.loadError" })));
        }
        if (rules.length === 0) {
            return (React.createElement(Layout, { className: "display-flex flex-direction-column centerContent" },
                React.createElement(Layout, { className: "padding-all-gutter" },
                    React.createElement(Headline, { size: "medium", margin: "none" },
                        React.createElement(FormattedMessage, { id: "ui-czna-authorities.derivationRules.list.empty" })))));
        }
        return (
        /* @ts-expect-error - stripes MCL types require all entity keys but only visible columns are needed */
        React.createElement(MultiColumnList, { contentData: rules, columnMapping: columnMapping, columnWidths: COLUMN_WIDTHS, visibleColumns: ['enabled', 'vocabularyType', 'field', 'thresholdStrategy', 'idPrefix', 'updatedAt', 'deriveNow'], formatter: formatter, nonInteractiveHeaders: ['enabled', 'vocabularyType', 'field', 'thresholdStrategy', 'idPrefix', 'updatedAt', 'deriveNow'], selectedRow: selectedId ? { id: selectedId } : undefined, onRowClick: handleRowClick }));
    };
    var detailPane = (isCreating || selectedId) ? (React.createElement(DerivationRuleDetail, { key: isCreating ? 'create' : selectedId, mode: isCreating ? 'create' : 'edit', id: isCreating ? undefined : selectedId, onClose: closeDetail, onSaved: function (saved) {
            var _a;
            (_a = calloutRef.current) === null || _a === void 0 ? void 0 : _a.sendCallout({
                type: 'success',
                message: intl.formatMessage({ id: 'ui-czna-authorities.derivationRules.save.success' }),
            });
            openRule(saved);
        }, onDeleted: function () {
            var _a;
            (_a = calloutRef.current) === null || _a === void 0 ? void 0 : _a.sendCallout({
                type: 'success',
                message: intl.formatMessage({ id: 'ui-czna-authorities.derivationRules.delete.success' }),
            });
            closeDetail();
        } })) : undefined;
    var lastMenu = (React.createElement(PaneMenu, null,
        React.createElement(PaneHelpButton, null,
            React.createElement(DerivationHelpContent, null)),
        canWrite && (React.createElement(Button, { buttonStyle: "primary", marginBottom0: true, onClick: openCreate },
            React.createElement(FormattedMessage, { id: "ui-czna-authorities.derivationRules.action.create" })))));
    return (React.createElement(React.Fragment, null,
        React.createElement(Paneset, null,
            filterPaneVisible && (React.createElement(Pane, { id: "derivation-rules-filter-pane", defaultWidth: "22%", paneTitle: React.createElement(FormattedMessage, { id: "ui-czna-authorities.pane.navigation" }), dismissible: true, onClose: function () { return setFilterPaneVisible(false); } },
                React.createElement(NavPaneLayout, null,
                    React.createElement("hr", { style: { border: 'none', borderTop: '1px solid #d9d9d9', margin: '12px 0' } }),
                    React.createElement(AccordionSet, null,
                        React.createElement(Accordion, { label: intl.formatMessage({ id: 'ui-czna-authorities.search' }), separator: false },
                            React.createElement(SearchField, { autoFocus: true, name: "query", value: query, onChange: function (e) { return setQuery(e.target.value); }, onClear: function () { return setQuery(''); }, placeholder: intl.formatMessage({ id: 'ui-czna-authorities.derivationRules.search.placeholder' }) })))))),
            React.createElement(Pane, { id: "derivation-rules-content-pane", defaultWidth: "fill", paneTitle: React.createElement(FormattedMessage, { id: "ui-czna-authorities.derivationRules.title" }), paneSub: intl.formatMessage({ id: 'ui-czna-authorities.derivationRules.resultCount' }, { count: rules.length }), lastMenu: lastMenu }, renderContent()),
            detailPane),
        React.createElement(Callout, { ref: calloutRef }),
        React.createElement(ConfirmationModal, { open: !!derivePreview && !!deriveRule, heading: intl.formatMessage({ id: 'ui-czna-authorities.derivationRules.confirmDerive.heading' }), message: derivePreview ? intl.formatMessage({ id: 'ui-czna-authorities.derivationRules.confirmDerive.message' }, {
                created: derivePreview.termsCreated,
                existing: derivePreview.termsAlreadyPresent,
                rematched: derivePreview.componentsRematched,
            }) : '', onConfirm: handleConfirmDeriveRow, onCancel: handleCancelDeriveRow, confirmLabel: intl.formatMessage({ id: 'ui-czna-authorities.derivationRules.action.confirm' }), cancelLabel: intl.formatMessage({ id: 'ui-czna-authorities.derivationRules.action.cancel' }), isConfirmButtonDisabled: deriveMutation.isLoading, buttonStyle: "primary" }),
        deriveError && (React.createElement(MessageBanner, { type: "error", dismissible: true, onExited: function () { return setDeriveError(null); } }, deriveError))));
};
export default DerivationRulesList;
